/**
 * Aerodrome position heuristic — INFERRED, best-effort only, documented in
 * full in docs/formats/world-terrain.md's scale-inference addendum.
 *
 * world-terrain.md §2.4 leaves the `AERO.DAT` `mapX`/`mapY` <-> `.WLD` `x`/`y`
 * relationship explicitly unresolved: no byte-level transform between the two
 * coordinate systems is in the corpus. This app still needs *some* position
 * for each aerodrome to draw a map marker and to pick a `/fly` start point,
 * so it fits one: each region's aerodromes' own `mapX`/`mapY` bounding box is
 * stretched onto that region's placement `x`/`y` bounding box, axis by axis.
 * This assumes only that the *spread* of aerodromes across a region roughly
 * tracks the *spread* of placed scenery across it (both track real
 * settlement/infrastructure density) — a materially weaker assumption than
 * trying to recover the map image's absolute pixel scale or origin, and it
 * needs no assumption about which axis is "north" (`placement-transform.ts`
 * doesn't resolve that either, and this file stays consistent with it by not
 * trying).
 *
 * Read the result as "roughly this part of the region", not a decoded
 * position — callers that render it should visually mark it as approximate.
 *
 * ## The bounding-box fit alone is not safe to stand on (2026-08-23)
 *
 * `docs/engineering/london-sea-placements.md` §3.1: the fit's failure mode is
 * structural. Three of London's four fields share `mapY = 7`, the axis
 * *minimum*, so the fit pins all three to `bounds.minY` — and a bounding box's
 * edge is by construction the emptiest part of a region. For London that edge
 * is 10-20 km out in the North Sea, which put all four fields, `/fly`'s
 * recentre point, the player's spawn, the flak belt and every searchlight in
 * the water.
 *
 * The fix is not a better fit — it is **anchoring**. `anchorAerodromes` below
 * keeps the projection's *scale*, which is sound (§3.1 measured it at 2.04 and
 * 1.98 km per map unit against a real-geography 2.03), and moves each field
 * onto the nearest piece of real, dry scenery. An aerodrome is an open field
 * beside a town; the towns are the one thing in the region whose positions are
 * decoded rather than fitted.
 *
 * **Why not refit the transform against `MAPn.DAT` instead** (the fix plan's
 * option 1b): it does not hold up. `world-terrain.md` §2.5 already found the
 * briefing maps' tagged-marker counts do not match `AERO.DAT`'s per-region
 * aerodrome counts, so there is no correspondence to fit. Measured directly
 * for this lane: an unconstrained per-region similarity fit of the aerodrome
 * constellation onto each `MAPn.DAT` marker cloud reaches 3/4-8/9 "inliers" at
 * 2 km, but only by shrinking the constellation — the recovered scales are
 * 502-1443 m per map unit with mutually contradictory axis signs, against the
 * 2 030 m real geography gives. Constrained to one shared scale and one axis
 * sense across all five regions (a real decode's signature), the best score
 * over the whole 600-3 200 m/unit band is 23/37 against 20/37 for a control
 * whose constellations are deliberately rotated 90° — no peak, no signal.
 * `world-terrain.md` §2.4 stays open.
 */

import type { PlacementOrigin } from "./placement-transform.ts";
import { wldUnitsToMetres } from "./wld-units.ts";

export interface AerodromeMapPoint {
  readonly name: string;
  readonly mapX: number;
  readonly mapY: number;
}

export interface WorldBounds {
  readonly minX: number;
  readonly maxX: number;
  readonly minY: number;
  readonly maxY: number;
}

/** A projected aerodrome position, in the same raw `.WLD` units as `WorldBounds`/placement `x`/`y` — callers convert to metres themselves (`wld-units.ts`). */
export interface ProjectedAerodrome {
  readonly name: string;
  readonly x: number;
  readonly y: number;
}

/** Guards a degenerate (single-point, or all-equal) map-pixel span from a divide-by-zero. */
function safeSpan(span: number): number {
  return span > 0 ? span : 1;
}

/**
 * Fits `aerodromes`' own `mapX`/`mapY` bounding box onto `bounds` axis by
 * axis. A region with zero or one aerodrome still returns one entry per
 * input (a lone aerodrome lands at `(bounds.minX, bounds.minY)` — an
 * arbitrary but stable placement for a case with no spread to fit against).
 */
export function projectAerodromes(aerodromes: readonly AerodromeMapPoint[], bounds: WorldBounds): ProjectedAerodrome[] {
  if (aerodromes.length === 0) return [];

  const mapXs = aerodromes.map((a) => a.mapX);
  const mapYs = aerodromes.map((a) => a.mapY);
  const mapMinX = Math.min(...mapXs);
  const mapMinY = Math.min(...mapYs);
  const scaleX = (bounds.maxX - bounds.minX) / safeSpan(Math.max(...mapXs) - mapMinX);
  const scaleY = (bounds.maxY - bounds.minY) / safeSpan(Math.max(...mapYs) - mapMinY);

  return aerodromes.map((a) => ({
    name: a.name,
    x: bounds.minX + (a.mapX - mapMinX) * scaleX,
    y: bounds.minY + (a.mapY - mapMinY) * scaleY,
  }));
}

/** Finds one projected aerodrome by name (case-sensitive — names come straight from `AERO.DAT`, already stable). */
export function findProjectedAerodrome(
  projected: readonly ProjectedAerodrome[],
  name: string,
): ProjectedAerodrome | undefined {
  return projected.find((a) => a.name === name);
}

/** A point on the ground plane in **world** metres (`x` east, `z` the flipped `.WLD` `y` — `placement-transform.ts`). */
export interface GroundSite {
  readonly x: number;
  readonly z: number;
}

/** Just enough of `world/water-mask.ts` for this module to stay free of it. */
export interface WaterTest {
  isWater(x: number, z: number): boolean;
}

/** Nowhere is wet — the answer for a region whose water could not be read, and the default. */
export const NO_WATER: WaterTest = { isWater: () => false };

/**
 * How much dry ground a mission needs around the point the region is recentred
 * on. Everything a generated mission puts on the ground is laid out relative
 * to that point, and the furthest any of it reaches is the ground-start
 * aerodrome at `AERODROME_Z_M` (4 200 m) plus its own 600 m circle — so 5 km
 * covers the flak belt (±3 km × 1.5 km), the home-defence gun ring
 * (`FLAK_RING_RADIUS_M`, 3 km) and the ground start alike.
 */
export const MISSION_GROUND_RADIUS_M = 5_000;

/**
 * The dry ground an ordinary aerodrome needs — its own graded apron
 * (`AERODROME_FLAT_M`), and nothing beyond it. Deliberately much smaller than
 * `MISSION_GROUND_RADIUS_M`: only the field the region is *recentred* on has a
 * whole mission laid out around it. Insisting every field have five dry
 * kilometres would drag markers tens of kilometres past a perfectly good
 * riverside town to reach open country — measured, before this split: Verdun's
 * Maxville anchored 41 km away.
 */
export const AERODROME_GROUND_RADIUS_M = 2_200;

/** Lattice step the disc is probed on. 116 samples inside a 5 km disc — fine enough to catch a bay, coarse enough to run over every candidate. */
const PROBE_STEP_M = 800;

/**
 * The share of a `MISSION_GROUND_RADIUS_M` disc around `(x, z)` that is dry,
 * `1` for a site with no water anywhere near it. This — not "is this one point
 * dry" — is the test a recentre point has to pass: a spawn one metre inland of
 * a beach would still lay its whole flak belt in the sea.
 */
export function dryShare(water: WaterTest, x: number, z: number, radiusM = MISSION_GROUND_RADIUS_M): number {
  const steps = Math.floor(radiusM / PROBE_STEP_M);
  let probed = 0;
  let dry = 0;
  for (let row = -steps; row <= steps; row += 1) {
    for (let column = -steps; column <= steps; column += 1) {
      const dx = column * PROBE_STEP_M;
      const dz = row * PROBE_STEP_M;
      if (dx * dx + dz * dz > radiusM * radiusM) continue;
      probed += 1;
      if (!water.isWater(x + dx, z + dz)) dry += 1;
    }
  }
  return probed === 0 ? 1 : dry / probed;
}

/** A projected aerodrome moved onto real ground, in world metres. */
export interface AnchoredAerodrome extends GroundSite {
  readonly name: string;
  /** How far the anchor moved the projection, in metres — the honest size of the heuristic's error at this field. */
  readonly anchoredByM: number;
}

/** Ties in the dry share are meaningless below this; anything closer counts as equally dry and distance decides. */
const DRY_SHARE_EPSILON = 0.005;

/**
 * How dry an ordinary field's apron has to be for the fit to be left alone. A
 * river crossing a 2.2 km apron costs a few per cent; the sea costs all of it,
 * so this separates "the fit is fine" from "the fit put a landing ground in
 * the North Sea" without pretending to a precision the fit does not have.
 */
const DRY_APRON_MIN = 0.8;

/**
 * The apron share alone is not enough: `dryShare` probes a 2.2 km disc on an
 * 800 m lattice, which is 13 samples, so a field can score 12/13 = 0.92 with
 * the one wet sample being **its own centre**. That is exactly what Verdun's
 * Sivry did once `world/water-mask.ts` became a topmost-surface test and the
 * rivers stopped being over-reported — a marker sitting in the Meuse with a
 * perfectly dry apron around it. A landing ground's own point has to be dry
 * whatever the disc says, so the two tests are `&&`-ed.
 */
function apronIsAcceptable(water: WaterTest, x: number, z: number): boolean {
  return !water.isWater(x, z) && dryShare(water, x, z, AERODROME_GROUND_RADIUS_M) >= DRY_APRON_MIN;
}

/**
 * The nearest `sites` entry to `(x, z)` with dry ground around it.
 *
 * Ranking is **dry share first, then distance**: a site whose `radiusM` disc
 * is entirely dry beats a wetter one however far away it is, and among equally
 * dry sites the nearest wins. `taken` sites are skipped so two aerodromes
 * never collapse onto one town. `undefined` when every site is taken (or there
 * are none), which leaves the projection where it was.
 */
export function anchorSite(
  x: number,
  z: number,
  sites: readonly GroundSite[],
  water: WaterTest,
  radiusM: number,
  taken: ReadonlySet<GroundSite> = new Set(),
  shares: Map<GroundSite, number> = new Map(),
): { readonly site: GroundSite; readonly distanceM: number } | undefined {
  const shareOf = (site: GroundSite): number => {
    let share = shares.get(site);
    if (share === undefined) {
      share = dryShare(water, site.x, site.z, radiusM);
      shares.set(site, share);
    }
    return share;
  };

  let best: { site: GroundSite; share: number; distanceM: number } | undefined;
  for (const site of sites) {
    if (taken.has(site)) continue;
    const distanceM = Math.hypot(site.x - x, site.z - z);
    // Cheap rejection first: a further site can only win on dryness, so only
    // score its disc when it could still displace the incumbent.
    if (best && best.share >= 1 - DRY_SHARE_EPSILON && distanceM >= best.distanceM) continue;
    const share = shareOf(site);
    if (
      !best ||
      share > best.share + DRY_SHARE_EPSILON ||
      (share > best.share - DRY_SHARE_EPSILON && distanceM < best.distanceM)
    ) {
      best = { site, share, distanceM };
    }
  }
  return best ? { site: best.site, distanceM: best.distanceM } : undefined;
}

/**
 * Moves projected fields onto real, dry ground.
 *
 * `sites` are the region's own scenery placements in world metres — the towns,
 * whose positions are decoded rather than fitted. Two rules, and the split
 * matters:
 *
 * - **`startAerodrome` is always anchored**, first, and against the full
 *   `MISSION_GROUND_RADIUS_M` disc: it becomes the region's origin, and a
 *   whole mission is laid out around it. Even where the fit already lands on
 *   dry ground this pulls the origin off the bounding-box edge it was pinned
 *   to and onto real scenery, which is the fix.
 * - **Every other field keeps its fitted position unless that position is
 *   wet** — its own point in water, or an apron more than `1 - DRY_APRON_MIN`
 *   water (`apronIsAcceptable`). These are map markers and graded aprons, not
 *   the origin, and the fit is all the evidence there is for where they are;
 *   moving a field that is already on land would be inventing precision.
 *   Measured over the corpus this anchors London's Ashford, Hainault Farm and
 *   Rochford, Dunkirk's Courtrai and La Gorgue, Somme's Cachy, Paris's
 *   St. Quentin and Verdun's St. Mihiel and Sivry.
 *
 * A region with no sites at all (or no water data) leaves every projection
 * untouched, which is exactly the behaviour every region had before this
 * existed.
 */
export function anchorAerodromes(
  projected: readonly ProjectedAerodrome[],
  sites: readonly GroundSite[],
  water: WaterTest = NO_WATER,
  startAerodrome?: string,
): AnchoredAerodrome[] {
  const taken = new Set<GroundSite>();
  const missionShares = new Map<GroundSite, number>();
  const apronShares = new Map<GroundSite, number>();
  const anchored: AnchoredAerodrome[] = Array.from({ length: projected.length });

  // Start field first, so it gets first refusal on the ground it needs; the
  // result is still returned in the caller's order, which is `AERO.DAT`'s.
  const startIndex = projected.findIndex((field) => field.name === startAerodrome);
  const order = projected.map((_field, index) => index).sort((a, b) => Number(b === startIndex) - Number(a === startIndex));
  for (const index of order) {
    const field = projected[index]!;
    const isStart = index === startIndex;
    const x = wldUnitsToMetres(field.x);
    const z = -wldUnitsToMetres(field.y);
    if (!isStart && apronIsAcceptable(water, x, z)) {
      anchored[index] = { name: field.name, x, z, anchoredByM: 0 };
      continue;
    }
    const best = anchorSite(
      x,
      z,
      sites,
      water,
      isStart ? MISSION_GROUND_RADIUS_M : AERODROME_GROUND_RADIUS_M,
      taken,
      isStart ? missionShares : apronShares,
    );
    if (!best) {
      anchored[index] = { name: field.name, x, z, anchoredByM: 0 };
      continue;
    }
    taken.add(best.site);
    anchored[index] = { name: field.name, x: best.site.x, z: best.site.z, anchoredByM: best.distanceM };
  }
  return anchored;
}

/** A world-metre ground point as the `.WLD`-axis origin `placementWorldTransform` recentres against. */
export function originOfSite(site: GroundSite): PlacementOrigin {
  return { x: site.x, z: -site.z };
}

/**
 * The recentring point a region is drawn around: its named start aerodrome's
 * projected position, in metres on the `.WLD` axes
 * (`placement-transform.ts`'s `PlacementOrigin`). `{ x: 0, z: 0 }` when the
 * region has no aerodrome by that name, which leaves the region in its own
 * raw frame rather than shifting it somewhere arbitrary.
 *
 * Lives here rather than in `flight/fly-region.ts` because two consumers need
 * the *same* answer and only one of them may touch three.js: the flight
 * scene recentres its scenery on it, and `map/map-model.ts` needs it to put
 * the aeroplane's recentred world position back into the region's own frame.
 * One function, so the two can never drift by an aerodrome.
 */
export function regionOrigin(
  aerodromes: readonly AerodromeMapPoint[],
  bounds: WorldBounds,
  startAerodrome: string,
): PlacementOrigin {
  const start = findProjectedAerodrome(projectAerodromes(aerodromes, bounds), startAerodrome);
  return start ? { x: wldUnitsToMetres(start.x), z: wldUnitsToMetres(start.y) } : { x: 0, z: 0 };
}
